// 機体 (AutoFork) との MQTT 通信サービス。
//
// fork_webapp_react の mqttService をプレイバック一覧取得に必要な範囲で移植・簡略化したもの。
// - クラウド経由: Cognito Identity Pool の一時認証情報で SigV4 署名付き WebSocket を生成し
//   AWS IoT Core に接続する。トピックは fork/{siteId}/{robotId}/#。
// - ローカル MQTT (開発): VITE_MQTT_HOST / VITE_MQTT_PORT のブローカーへ直接接続する。
//
// プレイバック一覧は service/request トピックへ getMap リクエストを送り、
// service/response で受け取ったマップの記録ルート (rec-1/rec-2 スロット) から抽出する。

import mqtt, { type MqttClient, type IClientOptions } from 'mqtt'
import { Sha256 } from '@aws-crypto/sha256-js'
import { getIotConfig } from '@/config/iot'
import { apiCall } from '@/api/client'
import type { MapCanvasParams, RouteMap } from '@/types'

export type ConnectState = 'disconnected' | 'connecting' | 'connected'

// サービスリクエスト/レスポンスのタイムアウト (ミリ秒)
const SERVICE_REQUEST_TIMEOUT = 10000
// MQTT 接続のタイムアウト (ミリ秒)
const CONNECTION_TIMEOUT = 10000

interface AwsCredentials {
  accessKeyId: string
  secretAccessKey: string
  sessionToken: string
  expiration: Date
}

interface ServiceRequest {
  requestId: string
  service: 'getMap' | 'getParam' | 'getBgImage' | 'getBgImageChunks'
  params?: { name?: string; imageId?: string; indices?: number[] }
}

interface ServiceResponse {
  requestId: string
  success: boolean
  data?: unknown
  error?: string
}

// 背景画像のチャンク分割メタ情報 (fork/.../bg_image/meta)
interface BgImageMeta {
  imageId: string
  totalSize: number
  chunkCount: number
  chunkSize: number
  mimeType?: string
  // 'http': 通知のみ（本体は HTTP 取得を想定、チャンクは続かない）。
  // 'chunks' / 未指定: 直後にチャンクが届く
  transport?: 'http' | 'chunks'
}

// getMap レスポンスの生データ形式
interface RawMapData {
  stations: Array<{ id: string; pose: { x: number; y: number; theta: number } }>
  slots: Array<{
    id: string
    stationId: string
    height: number
    type?: number
  }>
  guidelines: Array<{
    startId: string
    endId: string
    path?: [number, number][]
  }>
}

type PendingRequest = {
  resolve: (value: unknown) => void
  reject: (reason: Error) => void
  timeout: ReturnType<typeof setTimeout>
}

class MqttService {
  private client: MqttClient | null = null
  private connectState: ConnectState = 'disconnected'
  private connectionCallbacks = new Set<(state: ConnectState) => void>()
  private pendingRequests = new Map<string, PendingRequest>()
  private connectionTimeoutId: ReturnType<typeof setTimeout> | null = null

  // 接続中の機体 (トピック・clientId 構築に使用)
  private siteId = ''
  private topicPrefix = ''

  // AWS IoT 一時認証情報のキャッシュ (API サーバー発行)。
  private awsCredentials: AwsCredentials | null = null
  // API から返る AWS IoT エンドポイント (未取得時は env フォールバック)。
  private iotEndpoint = ''
  private requestCounter = 0

  // 背景画像
  private bgImageCallbacks = new Set<(data: ArrayBuffer) => void>()
  private lastBgImageData: ArrayBuffer | null = null

  // 背景画像のチャンク分割リアセンブリ用
  private chunkImageMeta: BgImageMeta | null = null
  private chunkImageData = new Map<number, ArrayBuffer>()
  private chunkImageTimeoutId: ReturnType<typeof setTimeout> | null = null
  private readonly CHUNK_REASSEMBLY_TIMEOUT = 30000
  // チャンク到着が途切れたときの欠損再要求 (fork_webapp_react と同じ挙動)。
  // AWS IoT は受信側ソケットが詰まると QoS0 配信を破棄するため
  // ("Client endpoint is not writable")、一部チャンクが届かないことがある
  private chunkStallTimeoutId: ReturnType<typeof setTimeout> | null = null
  private chunkResendAttempts = 0
  private readonly CHUNK_STALL_TIMEOUT = 3000
  private readonly CHUNK_RESEND_MAX_ATTEMPTS = 3

  // 記録シナリオ (recorded_scenario トピック)
  private recordedScenarioCallbacks = new Set<(data: string) => void>()

  getConnectState(): ConnectState {
    return this.connectState
  }

  onConnectionChange(callback: (state: ConnectState) => void): () => void {
    callback(this.connectState)
    this.connectionCallbacks.add(callback)
    return () => this.connectionCallbacks.delete(callback)
  }

  private setConnectState(state: ConnectState): void {
    if (this.connectState === state) return
    this.connectState = state
    this.connectionCallbacks.forEach((cb) => cb(state))
  }

  // 指定した機体へ接続する。AWS IoT が設定済みならクラウド経由、そうでなければローカル MQTT。
  // クラウド経由の一時認証情報は API サーバー (/api/iot/sites/{site}/credentials) から取得する。
  connect(siteId: string, robotId: string): void {
    if (this.connectState !== 'disconnected') {
      this.disconnect()
    }
    this.siteId = siteId
    // サイトが変わると認証情報を取り直す必要があるためリセットする。
    this.awsCredentials = null
    this.setConnectState('connecting')

    const config = getIotConfig()
    if (config.awsIotEnabled) {
      // AWS IoT では機体ごとに fork/{siteId}/{robotId} で名前空間を分ける。
      this.topicPrefix = `fork/${siteId}/${robotId}`
      void this.connectToAwsIot()
    } else {
      // ローカル MQTT ブローカー (機体に直接接続) では prefix なしの fork/...。
      this.topicPrefix = 'fork'
      this.connectToLocalBroker(config.mqttHost, config.mqttPort)
    }
  }

  disconnect(): void {
    this.clearConnectionTimeout()
    this.rejectAllPending('Disconnected')
    if (this.client) {
      try {
        this.client.end(true)
      } catch {
        // ignore
      }
      this.client = null
    }
    this.lastBgImageData = null
    this.setConnectState('disconnected')
  }

  // ---- 接続処理 ----

  private connectToLocalBroker(host: string, port: string): void {
    const url = `ws://${host}:${port}`
    const options: IClientOptions = {
      clientId: `fork_webapp_playback_${Math.random().toString(16).slice(2, 10)}`,
      keepalive: 30,
      connectTimeout: 4000,
      reconnectPeriod: 0,
    }
    this.startConnectionTimeout()
    try {
      this.client = mqtt.connect(url, options)
      this.registerClientHandlers()
    } catch (err) {
      console.error('Failed to connect to local MQTT broker:', err)
      this.handleDisconnection()
    }
  }

  private async connectToAwsIot(): Promise<void> {
    const signedUrl = await this.getSignedAwsIotUrl()
    if (!signedUrl) {
      console.error('Failed to build AWS IoT signed URL')
      this.handleDisconnection()
      return
    }
    // clientId は AWS IoT の IAM ポリシー (iot:Connect の Resource:
    // client/fork_webapp_*) に合わせて fork_webapp_ で始める必要がある。
    // (fork_webapp_backend/terraform/modules/cognito-iot 参照)
    const clientId = `fork_webapp_playback_${this.siteId}_${Date.now()}`
    const options: IClientOptions = {
      clientId,
      keepalive: 30,
      connectTimeout: 10000,
      reconnectPeriod: 0,
      protocolVersion: 4,
    }
    this.startConnectionTimeout()
    try {
      this.client = mqtt.connect(signedUrl, options)
      this.registerClientHandlers()
    } catch (err) {
      console.error('Failed to connect to AWS IoT:', err)
      this.handleDisconnection()
    }
  }

  private registerClientHandlers(): void {
    if (!this.client) return

    this.client.on('connect', () => {
      this.clearConnectionTimeout()
      this.setConnectState('connected')
      this.subscribe()
    })

    this.client.on('message', (topic, payload) => {
      if (topic.endsWith('/service/response')) {
        this.handleServiceResponse(payload.toString())
      } else if (topic.endsWith('/recorded_scenario')) {
        this.handleRecordedScenario(payload.toString())
      } else if (topic.endsWith('/bg_image/meta')) {
        this.handleChunkMeta(payload.toString())
      } else if (topic.includes('/bg_image/chunk/')) {
        const match = topic.match(/\/bg_image\/chunk\/(\d+)$/)
        if (match) {
          this.handleChunkData(parseInt(match[1], 10), toArrayBuffer(payload))
        }
      } else if (topic.endsWith('/bg_image')) {
        this.handleBgImage(toArrayBuffer(payload))
      }
    })

    this.client.on('error', (err) => {
      console.error('MQTT connection error:', err)
      this.handleDisconnection()
    })

    this.client.on('close', () => {
      this.handleDisconnection()
    })
  }

  private subscribe(): void {
    if (!this.client) return
    // IAM ポリシーとの整合性のためワイルドカードで一括 subscribe する
    // (fork_webapp_react と同じ方式)。
    const wildcard = `${this.topicPrefix}/#`
    this.client.subscribe(wildcard, { qos: 0 }, (err) => {
      if (err) console.error(`Failed to subscribe to ${wildcard}:`, err)
    })
  }

  private handleDisconnection(): void {
    this.clearConnectionTimeout()
    this.rejectAllPending('Connection lost')
    this.setConnectState('disconnected')
  }

  private startConnectionTimeout(): void {
    this.clearConnectionTimeout()
    this.connectionTimeoutId = setTimeout(() => {
      console.warn('MQTT connection timeout')
      this.disconnect()
    }, CONNECTION_TIMEOUT)
  }

  private clearConnectionTimeout(): void {
    if (this.connectionTimeoutId) {
      clearTimeout(this.connectionTimeoutId)
      this.connectionTimeoutId = null
    }
  }

  // ---- サービスリクエスト ----

  onBgImageUpdate(callback: (data: ArrayBuffer) => void): () => void {
    this.bgImageCallbacks.add(callback)
    if (this.lastBgImageData) {
      callback(this.lastBgImageData)
    }
    return () => {
      this.bgImageCallbacks.delete(callback)
    }
  }

  // 直近に受信・結合した画像の imageId（transport=http 通知の重複判定用）
  private lastBgImageId: string | null = null

  private handleBgImage(data: ArrayBuffer): void {
    this.lastBgImageData = data
    this.bgImageCallbacks.forEach((cb) => cb(data))
  }

  // 背景画像のチャンク分割受信 (bg_image/meta + bg_image/chunk/N)。
  // 全チャンクが揃った時点で結合し handleBgImage で通知する。
  private handleChunkMeta(payload: string): void {
    try {
      const meta = JSON.parse(payload) as BgImageMeta
      // transport=http は通知のみでチャンクは続かない。このエディタは HTTP 取得を
      // 持たないので、画像が変わっていれば getBgImage でチャンク配信を明示要求する
      if (meta.transport === 'http') {
        if (meta.imageId && meta.imageId !== this.lastBgImageId) {
          this.getBgImage().catch(() => {})
        }
        return
      }
      // 異なる画像 ID の場合のみ既存チャンクをクリア（メタより先にチャンクが
      // 届くケースに備え、初回・クリア後はチャンクを保持する）。
      if (
        this.chunkImageMeta !== null &&
        this.chunkImageMeta.imageId !== meta.imageId
      ) {
        this.chunkImageData.clear()
        this.clearChunkTimeout()
        this.chunkResendAttempts = 0
      }
      this.chunkImageMeta = meta
      this.startChunkTimeout()
      this.checkChunkComplete()
      this.startChunkStallTimer()
    } catch (err) {
      console.error('Failed to parse bg_image chunk meta:', err)
    }
  }

  private handleChunkData(index: number, data: ArrayBuffer): void {
    this.chunkImageData.set(index, data)
    this.startChunkTimeout()
    this.checkChunkComplete()
    this.startChunkStallTimer()
  }

  // 受信が途切れたら欠損チャンクを再要求する（受信が続いている間は延長）
  private startChunkStallTimer(): void {
    this.clearChunkStallTimer()
    if (this.chunkImageMeta === null && this.chunkImageData.size === 0) return
    this.chunkStallTimeoutId = setTimeout(() => {
      this.chunkStallTimeoutId = null
      this.requestMissingChunks()
    }, this.CHUNK_STALL_TIMEOUT)
  }

  private clearChunkStallTimer(): void {
    if (this.chunkStallTimeoutId) {
      clearTimeout(this.chunkStallTimeoutId)
      this.chunkStallTimeoutId = null
    }
  }

  private requestMissingChunks(): void {
    if (this.chunkResendAttempts >= this.CHUNK_RESEND_MAX_ATTEMPTS) {
      console.error(
        `bg_image chunks still missing after ${this.chunkResendAttempts} resend requests, giving up`
      )
      this.clearChunkData()
      return
    }
    this.chunkResendAttempts++

    const meta = this.chunkImageMeta
    if (meta === null) {
      // メタが届いていない（先に落ちた）場合は全体を再要求する
      console.warn(
        `bg_image chunk meta missing (${this.chunkImageData.size} chunks buffered), requesting full image (attempt ${this.chunkResendAttempts})`
      )
      this.getBgImage().catch((err) => {
        console.warn('getBgImage (chunk recovery) failed:', err)
      })
      this.startChunkStallTimer()
      return
    }

    const missing: number[] = []
    for (let i = 0; i < meta.chunkCount; i++) {
      if (!this.chunkImageData.has(i)) missing.push(i)
    }
    if (missing.length === 0) return

    console.warn(
      `Missing ${missing.length}/${meta.chunkCount} bg_image chunks of ${meta.imageId} [${missing.join(',')}], requesting resend (attempt ${this.chunkResendAttempts})`
    )
    this.sendServiceRequest({
      requestId: this.generateRequestId(),
      service: 'getBgImageChunks',
      params: { imageId: meta.imageId, indices: missing },
    }).catch((err) => {
      console.warn('getBgImageChunks failed:', err)
    })
    this.startChunkStallTimer()
  }

  private checkChunkComplete(): void {
    if (!this.chunkImageMeta) return
    if (this.chunkImageData.size < this.chunkImageMeta.chunkCount) return

    const totalBuffer = new ArrayBuffer(this.chunkImageMeta.totalSize)
    const totalView = new Uint8Array(totalBuffer)
    let offset = 0
    for (let i = 0; i < this.chunkImageMeta.chunkCount; i++) {
      const chunk = this.chunkImageData.get(i)
      if (!chunk) {
        console.error(`Missing bg_image chunk ${i}`)
        this.clearChunkData()
        return
      }
      totalView.set(new Uint8Array(chunk), offset)
      offset += chunk.byteLength
    }
    this.lastBgImageId = this.chunkImageMeta.imageId
    this.clearChunkData()
    this.handleBgImage(totalBuffer)
  }

  private startChunkTimeout(): void {
    this.clearChunkTimeout()
    this.chunkImageTimeoutId = setTimeout(() => {
      console.warn('bg_image chunk reassembly timeout - clearing partial data')
      this.clearChunkData()
    }, this.CHUNK_REASSEMBLY_TIMEOUT)
  }

  private clearChunkTimeout(): void {
    if (this.chunkImageTimeoutId) {
      clearTimeout(this.chunkImageTimeoutId)
      this.chunkImageTimeoutId = null
    }
  }

  private clearChunkData(): void {
    this.clearChunkTimeout()
    this.clearChunkStallTimer()
    this.chunkImageMeta = null
    this.chunkImageData.clear()
    this.chunkResendAttempts = 0
  }

  async getBgImage(): Promise<void> {
    await this.sendServiceRequest({
      requestId: this.generateRequestId(),
      service: 'getBgImage',
    })
  }

  // ---- 記録シナリオ (map_command / recorded_scenario) ----

  onRecordedScenarioUpdate(callback: (data: string) => void): () => void {
    this.recordedScenarioCallbacks.add(callback)
    return () => {
      this.recordedScenarioCallbacks.delete(callback)
    }
  }

  private handleRecordedScenario(raw: string): void {
    // std_msgs/String 形式 { data: "..." } または直接文字列の両方に対応
    let data = raw
    try {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object' && 'data' in parsed) {
        data = typeof parsed.data === 'string' ? parsed.data : raw
      }
    } catch {
      // 生の文字列としてそのまま扱う
    }
    this.recordedScenarioCallbacks.forEach((cb) => cb(data))
  }

  // 機体側に記録シナリオの読み込みを要求する（結果は recorded_scenario で受信）。
  loadRecordedScenario(): void {
    this.publishMapCommand('load_recorded_scenario', [])
  }

  // 機体側に記録シナリオを保存する。
  saveRecordedScenario(scenarioJson: string): void {
    this.publishMapCommand('save_recorded_scenario', [scenarioJson])
  }

  // 記録ルート（__recorded/<folderName>）を機体側から削除する。
  removeRecordedMap(folderName: string): void {
    this.publishMapCommand('remove_recorded_map', [folderName])
  }

  // 記録ルートのスロット高さ (m) を変更する（slots.csv の height 列のみ書き換え）。
  setRecordedSlotHeight(
    folderName: string,
    slotId: string,
    height: number
  ): void {
    this.publishMapCommand('set_recorded_slot_height', [
      folderName,
      slotId,
      String(height),
    ])
  }

  // 機体側にマップの再読み込みを要求する（ルート再生成・削除の反映に必要）。
  reloadMap(mapId: string): void {
    this.publishMapCommand('reload_map', [mapId])
  }

  private publishMapCommand(command: string, args: string[]): void {
    this.publish(`${this.topicPrefix}/map_command`, { command, args })
  }

  // 軌道記録コマンド（マーク列からのルート再生成に使用）。
  // 応答は無く、結果は getMap のポーリングで確認する。
  publishTrajectoryCommand(command: string, args: string[]): void {
    this.publish(`${this.topicPrefix}/trajectory_command`, { command, args })
  }

  // 機体の現在のマップ ID（ROS パラメータ /map_id）を取得する。
  async getMapId(): Promise<string | null> {
    try {
      const value = await this.sendServiceRequest({
        requestId: this.generateRequestId(),
        service: 'getParam',
        params: { name: '/map_id' },
      })
      return typeof value === 'string' && value ? value : null
    } catch {
      return null
    }
  }

  async getCanvasParams(): Promise<MapCanvasParams> {
    const [
      canvasWidthMeter,
      imageOriginPixX,
      imageOriginPixY,
      imagePixelPerMeter,
    ] = await Promise.all([
      this.sendServiceRequest({
        requestId: this.generateRequestId(),
        service: 'getParam',
        params: { name: '/fork_webapp/canvas_width_meter' },
      }).catch(() => undefined),
      this.sendServiceRequest({
        requestId: this.generateRequestId(),
        service: 'getParam',
        params: { name: '/fork_webapp/image_origin_pix_x' },
      }).catch(() => undefined),
      this.sendServiceRequest({
        requestId: this.generateRequestId(),
        service: 'getParam',
        params: { name: '/fork_webapp/image_origin_pix_y' },
      }).catch(() => undefined),
      this.sendServiceRequest({
        requestId: this.generateRequestId(),
        service: 'getParam',
        params: { name: '/fork_webapp/image_pixel_per_meter' },
      }).catch(() => undefined),
    ])

    return {
      canvasWidthMeter:
        typeof canvasWidthMeter === 'number' ? canvasWidthMeter : 20,
      imageOriginPixX:
        typeof imageOriginPixX === 'number' ? imageOriginPixX : 64,
      imageOriginPixY:
        typeof imageOriginPixY === 'number' ? imageOriginPixY : 130,
      imagePixelPerMeter:
        typeof imagePixelPerMeter === 'number' ? imagePixelPerMeter : 6.2,
    }
  }

  // 機体のマップを取得し、一覧表示用の RouteMap に変換する。
  async getMap(): Promise<RouteMap> {
    const raw = (await this.sendServiceRequest({
      requestId: this.generateRequestId(),
      service: 'getMap',
    })) as RawMapData

    const routeMap: RouteMap = {
      stations: new Map(),
      slots: new Map(),
      guidelines: new Map(),
    }
    for (const station of raw.stations ?? []) {
      routeMap.stations.set(station.id, station)
    }
    for (const slot of raw.slots ?? []) {
      routeMap.slots.set(slot.id, { ...slot, type: slot.type ?? 0 })
    }
    for (const gl of raw.guidelines ?? []) {
      routeMap.guidelines.set(`${gl.startId}^${gl.endId}`, gl)
    }
    return routeMap
  }

  private sendServiceRequest(request: ServiceRequest): Promise<unknown> {
    return new Promise((resolve, reject) => {
      if (!this.client || this.connectState !== 'connected') {
        reject(new Error('MQTT not connected'))
        return
      }
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(request.requestId)
        reject(new Error(`Service request timeout: ${request.service}`))
      }, SERVICE_REQUEST_TIMEOUT)

      this.pendingRequests.set(request.requestId, { resolve, reject, timeout })
      this.publish(`${this.topicPrefix}/service/request`, request)
    })
  }

  private handleServiceResponse(raw: string): void {
    let response: ServiceResponse
    try {
      response = JSON.parse(raw) as ServiceResponse
    } catch {
      console.warn('Failed to parse service response')
      return
    }
    const pending = this.pendingRequests.get(response.requestId)
    if (!pending) return
    clearTimeout(pending.timeout)
    this.pendingRequests.delete(response.requestId)
    if (response.success) {
      pending.resolve(response.data)
    } else {
      pending.reject(new Error(response.error || 'Service request failed'))
    }
  }

  private publish(topic: string, data: object): void {
    if (!this.client || this.connectState !== 'connected') {
      console.warn('Cannot publish: MQTT not connected')
      return
    }
    this.client.publish(topic, JSON.stringify(data), { qos: 0 }, (err) => {
      if (err) console.error(`Failed to publish to ${topic}:`, err)
    })
  }

  private rejectAllPending(reason: string): void {
    for (const pending of this.pendingRequests.values()) {
      clearTimeout(pending.timeout)
      pending.reject(new Error(reason))
    }
    this.pendingRequests.clear()
  }

  private generateRequestId(): string {
    this.requestCounter += 1
    return `playback_editor_${this.requestCounter}_${this.connectState}`
  }

  // ---- AWS IoT 認証 (API サーバー発行の一時認証情報 + SigV4) ----

  // 接続先サイトに絞った一時認証情報を API サーバーから取得する。
  // (Cognito Identity Pool の代わり。サイト分離は API 側のセッションポリシーで担保)
  private async getIotCredentials(): Promise<AwsCredentials | null> {
    if (this.awsCredentials && this.awsCredentials.expiration > new Date()) {
      return this.awsCredentials
    }
    if (!this.siteId) {
      console.error('siteId not set; cannot request IoT credentials')
      return null
    }
    try {
      const resp = await apiCall(
        'POST',
        `/api/iot/sites/${encodeURIComponent(this.siteId)}/credentials`
      )
      if (!resp.ok) {
        console.error('Failed to get IoT credentials:', resp.status)
        return null
      }
      const data = await resp.json()
      const accessKeyId = data.access_key_id ?? data.accessKeyId
      const secretAccessKey = data.secret_access_key ?? data.secretAccessKey
      const sessionToken = data.session_token ?? data.sessionToken
      const expiration = data.expiration
      if (!accessKeyId || !secretAccessKey || !sessionToken) {
        console.error('IoT credentials response missing fields')
        return null
      }
      this.iotEndpoint = data.iot_endpoint ?? data.iotEndpoint ?? ''
      this.awsCredentials = {
        accessKeyId,
        secretAccessKey,
        sessionToken,
        expiration: expiration
          ? new Date(expiration)
          : new Date(Date.now() + 3600000),
      }
      return this.awsCredentials
    } catch (err) {
      console.error('Failed to get IoT credentials:', err)
      return null
    }
  }

  private async sha256(data: string): Promise<string> {
    const hash = new Sha256()
    hash.update(data)
    return toHex(await hash.digest())
  }

  private async hmacSha256(key: Uint8Array, data: string): Promise<Uint8Array> {
    const hash = new Sha256(key)
    hash.update(data)
    return hash.digest()
  }

  private async getSignatureKey(
    secretKey: string,
    dateStamp: string,
    region: string,
    service: string
  ): Promise<Uint8Array> {
    const kDate = await this.hmacSha256(
      new TextEncoder().encode('AWS4' + secretKey),
      dateStamp
    )
    const kRegion = await this.hmacSha256(kDate, region)
    const kService = await this.hmacSha256(kRegion, service)
    return this.hmacSha256(kService, 'aws4_request')
  }

  private async getSignedAwsIotUrl(): Promise<string | null> {
    const config = getIotConfig()
    const credentials = await this.getIotCredentials()
    // エンドポイントは API 応答を優先し、無ければ env フォールバック。
    const host = this.iotEndpoint || config.awsIotEndpoint
    if (!credentials || !host || !config.awsRegion) {
      return null
    }

    const region = config.awsRegion
    const service = 'iotdevicegateway'
    const method = 'GET'
    const canonicalUri = '/mqtt'
    const algorithm = 'AWS4-HMAC-SHA256'

    const now = new Date()
    const amzDate = now.toISOString().replace(/[:-]|\.\d{3}/g, '')
    const dateStamp = amzDate.slice(0, 8)
    const credentialScope = `${dateStamp}/${region}/${service}/aws4_request`

    // X-Amz-Security-Token は署名前のクエリには含めない
    const canonicalQueryParams = [
      `X-Amz-Algorithm=${algorithm}`,
      `X-Amz-Credential=${encodeURIComponent(`${credentials.accessKeyId}/${credentialScope}`)}`,
      `X-Amz-Date=${amzDate}`,
      `X-Amz-Expires=86400`,
      `X-Amz-SignedHeaders=host`,
    ]
      .sort()
      .join('&')

    const canonicalHeaders = `host:${host}\n`
    const signedHeaders = 'host'
    const payloadHash = await this.sha256('')

    const canonicalRequest = [
      method,
      canonicalUri,
      canonicalQueryParams,
      canonicalHeaders,
      signedHeaders,
      payloadHash,
    ].join('\n')

    const canonicalRequestHash = await this.sha256(canonicalRequest)
    const stringToSign = [
      algorithm,
      amzDate,
      credentialScope,
      canonicalRequestHash,
    ].join('\n')

    const signingKey = await this.getSignatureKey(
      credentials.secretAccessKey,
      dateStamp,
      region,
      service
    )
    const signatureHash = new Sha256(signingKey)
    signatureHash.update(stringToSign)
    const signature = toHex(await signatureHash.digest())

    let finalUrl = `wss://${host}${canonicalUri}?${canonicalQueryParams}&X-Amz-Signature=${signature}`
    if (credentials.sessionToken) {
      finalUrl += `&X-Amz-Security-Token=${encodeURIComponent(credentials.sessionToken)}`
    }
    return finalUrl
  }
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

// MQTT ペイロード (Buffer/Uint8Array) を独立した ArrayBuffer にコピーする。
function toArrayBuffer(payload: Uint8Array): ArrayBuffer {
  const buffer = new ArrayBuffer(payload.byteLength)
  new Uint8Array(buffer).set(payload)
  return buffer
}

const mqttService = new MqttService()
export default mqttService
